import { useQuery } from '@tanstack/react-query'
import { Icon, type IconName } from '../ui/Icon'
import { useArtifactsList } from '../artifacts/ArtifactsPage'
import { backedUpAgents } from '../backup/BackupsPage'
import { getBackups } from '../transport/relay'
import { useHeldTabs } from './heldTabs'
import { useAllBookmarks } from './BookmarksPage'
import { useAllBoards } from './WhiteboardsPage'
import { pageRooms, useAllPages } from './AllPagesPage'
import { pagesOn } from '../page/pages'
import type { AgentRow, ProjectRow } from '../transport/api'

/** The pages More opens on the stage (MoreStage). */
export type MorePage = 'artifacts' | 'tabs' | 'backups' | 'bookmarks' | 'boards' | 'pages'

/**
 * EVERYTHING THAT IS NOT AGENTS — as the RAIL'S OWN BODY, not a sheet over it.
 *
 * Owner, 2026-09-20: 「按下 More 之後,你不應該使用彈跳出來一個新的蓋版頁面的方式,
 * 而是直接讓整個面板的底板換掉。因為我們之後會在 More 的面板裡面加上其他的功能,
 * 不只是 Artifact…你把它彈跳出來,只使用 Artifact 的一個蓋板方式,這樣子就非常不符合
 * 使用者的直覺」.
 *
 * More is a PLACE. It swaps in for the rail's list exactly the way the main menu
 * already does — same flex slot as #sbList and #serverMenu — so the nav bar below
 * stays put. There is no close button, and that is the point of being a destination:
 * the nav pill is the way out, the same way Agents is the way out of Humans.
 *
 * ONE BUTTON PER PART — owner, 2026-10-22: 讓 Artifact, Chrome tabs, Backups 在左側頁面
 * 呈現一個按鈕就好. A button opens its part as a page of the STAGE (MoreStage): beside the
 * rail on a wide screen, over everything on a narrow one. The rail keeps only what says
 * which part is which and how much is in it — the chip, the name, the count — and marks
 * the one the stage is showing.
 *
 * The counts read the same queries the pages do, so a button and its page cannot disagree.
 */
export function MorePanel({
  page,
  onOpenPage,
  agents = [],
  projects = [],
}: {
  page: MorePage | null
  onOpenPage: (p: MorePage) => void
  /** The roster and the rooms: what Bookmarks and Whiteboards gather from. */
  agents?: AgentRow[]
  projects?: ProjectRow[]
}) {
  const artifacts = useArtifactsList()
  const tabs = useHeldTabs()
  const backups = useQuery({ queryKey: ['backups'], queryFn: getBackups, staleTime: 0 })

  const artifactCount = artifacts.data?.rows.length ?? 0
  const tabCount = (tabs.data?.browsers ?? []).reduce((n, b) => n + b.tabs.length, 0)
  const agentCount = backedUpAgents(backups.data?.backups ?? [])
  // Bookmarks live on every machine: counted from what has been read, and only once all of it has — asking every
  // machine just to put a number on a button is not worth it, and part of a count is a wrong one.
  const bookmarks = useAllBookmarks(agents, projects.filter((p) => !p.group_id), false)
  const boards = useAllBoards(projects)
  // Only in a build that offers pages (page/pages.ts pagesOn): a build without them asks no room for its pages.
  const withPages = pagesOn()
  const pages = useAllPages(withPages ? pageRooms(projects) : [])

  const parts: { id: MorePage; icon: IconName; chip: string; title: string; count?: string | undefined }[] = [
    { id: 'artifacts', icon: 'auto_awesome', chip: 'lp-mauve', title: 'Artifacts', count: artifactCount > 0 ? String(artifactCount) : undefined },
    { id: 'tabs', icon: 'tab', chip: 'lp-blue', title: 'Chrome tabs', count: tabCount > 0 ? String(tabCount) : undefined },
    {
      id: 'backups',
      icon: 'cloud_upload',
      chip: 'lp-peach',
      title: 'Backups',
      count: agentCount > 0 ? `${agentCount} agent${agentCount === 1 ? '' : 's'}` : undefined,
    },
    // Owner, 2026-11-01: 在 more … 新增 Bookmark … 集中的地方來管理 … Whiteboard 也要在 More 那邊也有一個地方.
    {
      id: 'bookmarks',
      icon: 'bookmarks',
      chip: 'lp-yellow',
      title: 'Bookmarks',
      count: bookmarks.complete && bookmarks.count > 0 ? String(bookmarks.count) : undefined,
    },
    { id: 'boards', icon: 'schema', chip: 'lp-green', title: 'Whiteboards', count: boards.rows.length > 0 ? String(boards.rows.length) : undefined },
    // Owner, 2026-11-03: 在 More 的欄位, 新增 page 的 item, 如同 whiteboard 一般 — the room's Pages button's blue.
    ...(withPages
      ? [{ id: 'pages' as const, icon: 'description' as const, chip: 'lp-blue', title: 'Pages', count: pages.rows.length > 0 ? String(pages.rows.length) : undefined }]
      : []),
  ]

  return (
    <nav id="sbMore" aria-label="More">
      <div className="more-body">
        {parts.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`more-card more-btn${page === p.id ? ' on' : ''}`}
            aria-current={page === p.id ? 'page' : undefined}
            onClick={() => onOpenPage(p.id)}
          >
            <span className={`lp-chip ${p.chip}`} aria-hidden="true">
              <Icon name={p.icon} size={14} />
            </span>
            <span className="more-btn-name">{p.title}</span>
            {p.count && <span className="more-count">· {p.count}</span>}
            <Icon name="chevron_right" size={18} />
          </button>
        ))}
      </div>
    </nav>
  )
}
